import { useState } from "react";
import { INavbarIconVariant, NavbarIcon } from "../../../atoms/navbar/Icon";
import { Asset, HuggingFaceModel } from "../../../models/types";
import { SmallModal } from "../../../components/SmallModal";
import { Box, Button, Stack, Typography } from "@mui/material";
import { SearchableDropdown } from "../../../components/SearchableDropdown";
import { searchHuggingFaceModels } from "../../../hooks/useHuggingFace";
import { searchAssetsWithParams } from "../../../hooks/useAssets";
import { LoadingButton } from "@mui/lab";
import { usePluginInfo } from "../../../hooks/usePlugins";
import { useNavigate } from "react-router-dom";

type AddFairoOrHuggingFaceModelProps = {
  open: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
};

export const AddFairoOrHuggingFaceModel = (props: AddFairoOrHuggingFaceModelProps) => {
  const { open, onClose, onChange } = props;
  const navigate = useNavigate();
  const { data: huggingFaceIntegration } = usePluginInfo("hugging_face");
  const isHuggingFaceAvailable = !!huggingFaceIntegration?.hugging_face_token;

  const sourceOptions: {
    name: string;
    icon: INavbarIconVariant;
  }[] = [
    {
      name: "Fairo",
      icon: "fairo-logo",
    },
    {
      name: "Hugging Face",
      icon: "hugging-face",
    },
  ];

  const [source, setSource] = useState<string | null>("Fairo");
  const [baseModel, setBaseModel] = useState<Asset | HuggingFaceModel | null>(null);

  const onSave = async () => {
    if (source === "Fairo") {
      onChange(
        JSON.stringify({
          type: "fairo",
          id: baseModel?.id,
          name: (baseModel as Asset)?.name,
        })
      );
    }
    if (source === "Hugging Face") {
      onChange(
        JSON.stringify({
          type: "hugging-face",
          id: baseModel?.id,
          name: baseModel?.id,
        })
      );
    }
    onClose();
  };

  return (
    <SmallModal open={open} onClose={onClose} size="small" title="Add Fairo or Hugging Face Model">
      <Stack gap="10px">
        <Typography variant="h3">Source</Typography>
        <SearchableDropdown<string>
          value={source}
          onChange={(option) => {
            if (option === "Hugging Face" && !isHuggingFaceAvailable) {
              navigate("/organization/integrations");
              return;
            }
            setSource(option);
          }}
          getOptions={(search: string) =>
            sourceOptions
              .filter((option) => option.name.toLowerCase().includes(search.toLowerCase()))
              .map((o) => o.name)
          }
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(a, b) => a === b}
          renderOption={(props, option) => {
            return (
              <Box
                component="li"
                {...props}
                sx={{ cursor: "pointer" }}
                alignItems="center"
                display="flex"
                flexDirection="row"
                gap="5px"
                width="100%"
              >
                <Box
                  display="flex"
                  flex={1}
                  flexDirection="row"
                  gap="5px"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Box display="flex" flexDirection="row" gap="5px" alignItems="center" flex={1}>
                    <NavbarIcon
                      variant={sourceOptions.find((o) => o.name === option)?.icon ?? "fairo-logo"}
                      sx={{
                        width: "20px",
                        height: "20px",
                        minHeight: "20px",
                        minWidth: "20px",
                        padding: option === "Fairo" ? "2.5px" : "",
                      }}
                      activeMLFlow={true}
                    />
                    <Typography variant="h4">{option}</Typography>
                  </Box>
                  {option === "Hugging Face" && !isHuggingFaceAvailable && (
                    <Button
                      sx={{ height: "30px" }}
                      onClick={() => navigate("/organization/integrations")}
                    >
                      Set Up Now
                    </Button>
                  )}
                </Box>
              </Box>
            );
          }}
        />
        <Typography variant="h3">Base Model</Typography>
        <SearchableDropdown
          value={baseModel}
          onChange={(option) => setBaseModel(option)}
          getOptions={
            source === "Fairo"
              ? searchAssetsWithParams({ "asset_type_name[]": ["Model"] })
              : source === "Hugging Face"
              ? searchHuggingFaceModels
              : async () => []
          }
          getOptionLabel={(option) =>
            source === "Fairo" && "name" in option ? option.name : option.id
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
        />
        <LoadingButton variant="contained" onClick={onSave} disabled={!source || !baseModel}>
          Save
        </LoadingButton>
      </Stack>
    </SmallModal>
  );
};
